if (!customElements.get('product-information')) {
  customElements.define('product-information', class ProductInformation extends HTMLElement {
    staticBuyTools = true;
    quantityInput = undefined;
    onVariantChangeUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;
    abortController = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();
      this.buyToolsTrigger = this.querySelector('#product__buy-tools-trigger');
      this.productInfoContainer = this.querySelector('.product__info-container');
      this.onScrollToolsHandler = this.onScrollBuyTools.bind(this);
      this.quantityInput = this.querySelector(`#QuantityInput-${this.sectionId}`);

      this.initBreakpoints();
    }

    initBreakpoints() {
      const self = this;

      enquire.register('screen and (max-width: 1000px)', {
        match: () => {
          self.bpSmall = true;
          this.productInfoContainer.classList.remove('product__info-container--modal--out');
          this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
        },
        unmatch: () => {
          self.bpSmall = false;
          this.productInfoContainer.classList.remove('product__info-container--modal--out');
          this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
        }
      });
    }

    connectedCallback() {
      this.initializeProductSwapUtility();

      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );
    }

    addPreProcessCallback(callback) {
      this.preProcessHtmlCallbacks.push(callback);
    }

    disconnectedCallback() {
      this.onVariantChangeUnsubscriber?.();
      this.abortController?.abort();
      // this.cartUpdateUnsubscriber?.();
    }

    initializeProductSwapUtility() {
      this.preProcessHtmlCallbacks.push((html) => {
        html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
      });
      this.postProcessHtmlCallbacks.push((newNode) => {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
      });
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      this.resetProductFormState();

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
          : this.handleUpdateProductInfo(productUrl),
      });
    }

    resetProductFormState() {
      const productForm = this.productForm;
      this.toggleSubmitButton(true);
      productForm?.handleErrorMessage();
    }

    handleSwapProduct(productUrl, updateFullPage) {
      return (html) => {
        this.productModal?.remove();

        const selector = updateFullPage ? "product-information[id^='MainProduct']" : 'product-information';
        const variant = this.getSelectedVariant(html.querySelector(selector));
        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            html.querySelector('product-information'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }
      };
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load this option');
          return response.text();
        })
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          Array.from(this.querySelectorAll('[id]')).find(node => node.id === targetId)?.focus();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            this.productForm?.handleErrorMessage(window.cartStrings.error);
            console.error(error);
          }
        });
    }

    getSelectedVariant(productInfoNode) {
      const selector = 'variant-selects [data-selected-variant]';

      const selectedVariant = productInfoNode.querySelector(`${selector}`)?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    updateOptionValues(html) {
      const sectionId = `${this.sectionId}${this.blockId ? `${this.blockId}`: ''}`;
      const variantSelects = html.getElementById(`VariantSelect-${sectionId}`);

      if (variantSelects) {
        const isOnProductModal =
          this.closest('complementary-products-card') ||
          this.closest('quick-add-modal') ||
          this.closest('.shop-look__item-wrap');
        if (isOnProductModal) {
          const updatedOptions = variantSelects.querySelectorAll('fieldset');

          this.variantSelectors.querySelectorAll('input[data-disabled="true"]').forEach((i) => {
            i.removeAttribute('data-disabled');
          });

          updatedOptions.forEach((option) => {
            option.querySelectorAll('input[data-disabled="true"]').forEach((i) => {
              const inputId = i.dataset.optionValueId;
              const currentNode = this.variantSelectors.querySelector(`input[data-option-value-id="${inputId}"]`);
              if (currentNode?.tagName === 'INPUT' && currentNode?.type === 'radio') {
                currentNode.setAttribute('data-disabled', 'true');
              }
            });
          });
        } else {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }
    }

    handleUpdateProductInfo(productUrl) {
      return (html) => {
        const variant = this.getSelectedVariant(html);
        const sectionId = `${this.sectionId}${this.blockId ? `${this.blockId}`: ''}`;

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant, variant?.featured_media?.id);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('visibility-hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('sku', ({ classList }) => classList.contains('visibility-hidden'));
        updateSourceFromDestination('StockNotification', ({ innerText }) => innerText === '');

        this.updateLimitQuantity(this.sectionId, html);

        this.toggleSubmitButton(
          html.getElementById(`ProductSubmitButton-${sectionId}`)?.hasAttribute('disabled') ?? true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });
      };
    }

    updateLimitQuantity(sectionId, html) {
      if (!this.quantityInput) return;
      const quantityFormUpdated = html.getElementById(`QuantityInput-${sectionId}`);

      this.quantityInput.updateLimitQuantity(quantityFormUpdated);
    }

    updateVariantInputs(variantId) {
      const sectionId = `${this.dataset.section}`;

      this.querySelectorAll(
        `#product-form-${sectionId}, #product-form-installment-${sectionId}`
      ).forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateURL(url, variantId) {
      this.querySelector('share-button')?.updateUrl(
        `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
      );

      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
    }

    toggleSubmitButton(disable = true, text) {
      const productFormElement = this.productForm;
      const productForm = productFormElement?.querySelector('form');
      if (!productForm) return;

      const addButtons = productForm.querySelectorAll('[data-add-to-cart]');
      if (!addButtons.length) return;

      const variantClassSoldOut = 'product-form--variant-sold-out';
      const formParentElement = productForm.closest('.product-form__buy-buttons');
      const productMetaPrice = formParentElement ? formParentElement.querySelector('[data-product-meta-price]') : null;

      addButtons.forEach((addButton) => {
        const addButtonText = addButton.querySelector('[data-add-to-cart-text]');
        // A variant update must not unlock a cart request that is still running.
        addButton.dataset.variantDisabled = String(disable);
        addButton.disabled = disable || Boolean(productFormElement.isLoading);
        if (addButton.disabled) addButton.setAttribute('aria-disabled', 'true');
        else addButton.removeAttribute('aria-disabled');

        const label = disable ? text : window.variantStrings.addToCart;
        if (label) {
          addButton.setAttribute('aria-label', label);
          if (addButtonText) addButtonText.textContent = label;
        }
      });

      if (!disable || text) {
        productForm.classList.toggle(variantClassSoldOut, disable);
        productMetaPrice?.classList.toggle('meta-price--hidden', disable);
      }
    }

    setUnavailable() {
      this.toggleSubmitButton(true, window.variantStrings.unavailable);

      const selectors = ['price', 'sku', 'StockNotification']
        .map((id) => `#${id}-${this.dataset.section}`)
        .join(', ');

      document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('visibility-hidden'));
    }

    updateMedia(variant, variantFeaturedMediaId) {
      const sectionId = `${this.dataset.section}`;
      const mediaGallery = this.querySelector(`#MediaGallery-${sectionId}`);
      if (!mediaGallery || (!variantFeaturedMediaId && !mediaGallery.colorPreviews)) return;
      mediaGallery.setActiveMedia(`${sectionId}-${variantFeaturedMediaId}`, variant);
    }

    buyToolsCalculations() {
      this.staticBuyTools = false;
      this.productPageSection = this.parentElement;
      this.productInfoWrapper = this.productPageSection.querySelector('.product__info-wrapper');
      this.productInfoContainerHeight = this.productInfoContainer.offsetHeight;
      this.negativeHeight = this.productInfoContainerHeight * -1;
      this.productContentSection = document.querySelector('.product-content-section');

      const productContentMinHeight = this.productInfoContainerHeight - this.productPageSection.offsetHeight + 54;
      this.productContentSection.style.minHeight = `${productContentMinHeight}px`;

      this.resizeProductInfoObserver = new ResizeObserver((entries) => {
        if (this.bpSmall) return;
        if (!this.productInfoContainer.classList.contains('product__info-container--modal')) {
          const productInfoContainerHeight = this.productInfoContainer.offsetHeight;
          if (productInfoContainerHeight != this.productInfoContainerHeight) {
            this.productInfoContainerHeight = productInfoContainerHeight;
            const productContentMinHeight = productInfoContainerHeight - this.productPageSection.offsetHeight + 54;
            this.productContentSection.style.minHeight = `${productContentMinHeight}px`;

            this.negativeHeight = productInfoContainerHeight * -1;
          }
        }
      });

      this.resizeProductInfoObserver.observe(this.productInfoContainer);
    }

    onScrollBuyTools() {
      if (this.bpSmall || !this.buyToolsTrigger ) return;

      const triggerTop = this.buyToolsTrigger.getBoundingClientRect().top;
      const buyToolsModalActive = this.productInfoContainer.classList.contains('product__info-container--modal', 'background-modal');
      const bytToolsModalOut = this.productInfoContainer.classList.contains('product__info-container--modal--out');

      if (triggerTop < this.negativeHeight && !buyToolsModalActive) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add('product__info-container--modal', 'background-modal');
        });
      } else if (triggerTop > this.negativeHeight && buyToolsModalActive && !bytToolsModalOut) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add('product__info-container--modal--out');

          this.productInfoContainer.addEventListener('animationend', () => {
            this.productInfoContainer.classList.remove('product__info-container--modal--out');
            this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
          }, { once: true });
        });
      }
    }

    get productForm() {
      return this.querySelector(`product-form`);
    }

    get productModal() {
      return document.querySelector(`#ProductModal-${this.sectionId}`);
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get variantSelectors() {
      return this.querySelector('variant-selects');
    }

    get relatedProducts() {
      const relatedProductsSectionId = SectionId.getIdForSection(
        SectionId.parseId(this.sectionId),
        'related-products'
      );
      return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.section;
    }

    get blockId() {
      return this.dataset.block;
    }

    get productId() {
      return this.dataset.productId;
    }
  });
}
